import DocumentTextField from "components/foundation/DocumentTextField/DocumentTextField";
import DocumentTextAreaField from "components/foundation/DocumentTextField/DocumentTextArea";
import React, { useRef } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { TempDocumentDetailResponse } from "apis/data-contracts";
import DatePicker from "components/foundation/DatePicker";
import styled from "styled-components";
import { METER_TO_PYEONG } from "config/constant";
import CreateDocumentForm from "../../CreateDocumentForm";
import CreateDocumentClassFormBody from "../CreateDocumentClassFormBody";
import { CONTRACT_INFO_STEP1_FIELD as FIELD } from "config/documentConstant";
import formatMinMaxLengthMessage from "utils/formatUtils/formatMinMaxLengthMessage";
import { get } from "lodash-es";
import formatNumber from "utils/formatUtils/formatNumber";

/* 수주정보관리 1단계 문서 */
interface ContractInfoStep1Form {
  [FIELD.PJ.key]: string;
  [FIELD.INFORMATION_PROVIDER.key]: string;
  [FIELD.RECEIPT_DATE.key]: string;
  [FIELD.BUSINESS_METHOD.key]: string;
  [FIELD.IMPLEMENTER_AND_RELATED_COMPANY.key]: string;
  [FIELD.LAND_AREA.key]: {
    landAreaInSquareMeters: number;
    landAreaInPyeong: number;
  };
  [FIELD.URBAN_MANAGEMENT_PLAN.key]: string;
  [FIELD.BUSINESS_SCALE.key]: string;
  [FIELD.ADDITIONAL_NOTES.key]: string;
}

interface ContractInfoStep1Props {
  tempDocumentDetail?: TempDocumentDetailResponse;
}

const ContractInfoStep1 = (_props: ContractInfoStep1Props) => {
  const {
    register,
    control,
    setValue,
    setError,
    clearErrors,
    watch,
    formState: { errors },
  } = useFormContext<ContractInfoStep1Form>();

  const pyeongRef = useRef<HTMLInputElement | null>(null);

  return (
    <CreateDocumentClassFormBody noBorderTop>
      <CreateDocumentForm.Field label={FIELD.PJ.label}>
        <DocumentTextField
          placeholder={FIELD.PJ.placeholder}
          {...register(FIELD.PJ.key, {
            required: "PJ명 입력 필수",
            minLength: {
              value: FIELD.PJ.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.PJ.minLength,
                FIELD.PJ.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.PJ.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.PJ.minLength,
                FIELD.PJ.maxLength,
              ),
            },
          })}
          errorMessage={errors[FIELD.PJ.key]?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.INFORMATION_PROVIDER.label}>
        <DocumentTextField
          placeholder={FIELD.INFORMATION_PROVIDER.placeholder}
          required={FIELD.INFORMATION_PROVIDER.required}
          {...register(FIELD.INFORMATION_PROVIDER.key, {
            required: "정보제공자입력 필수",
            minLength: {
              value: FIELD.INFORMATION_PROVIDER.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.INFORMATION_PROVIDER.minLength,
                FIELD.INFORMATION_PROVIDER.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.INFORMATION_PROVIDER.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.INFORMATION_PROVIDER.minLength,
                FIELD.INFORMATION_PROVIDER.maxLength,
              ),
            },
          })}
          errorMessage={errors[FIELD.INFORMATION_PROVIDER.key]?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.RECEIPT_DATE.label}>
        <Controller
          name={FIELD.RECEIPT_DATE.key}
          control={control}
          rules={{
            required: "접수일 입력 필수",
          }}
          render={({ field }) => (
            <DatePicker
              {...field}
              usage="document"
              placeholder={FIELD.RECEIPT_DATE.placeholder}
              dateFormat={FIELD.RECEIPT_DATE.dateFormat}
              onChangeDate={(_, dateString) => {
                field.onChange(dateString);
              }}
              errorMessage={errors[FIELD.RECEIPT_DATE.key]?.message}
            />
          )}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.BUSINESS_METHOD.label}>
        <DocumentTextField
          placeholder={FIELD.BUSINESS_METHOD.placeholder}
          required={FIELD.BUSINESS_METHOD.required}
          {...register(FIELD.BUSINESS_METHOD.key, {
            required: "사업방식 입력 필수",
            minLength: {
              value: FIELD.BUSINESS_METHOD.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.BUSINESS_METHOD.minLength,
                FIELD.BUSINESS_METHOD.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.BUSINESS_METHOD.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.BUSINESS_METHOD.minLength,
                FIELD.BUSINESS_METHOD.maxLength,
              ),
            },
          })}
          errorMessage={errors[FIELD.BUSINESS_METHOD.key]?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field
        label={FIELD.IMPLEMENTER_AND_RELATED_COMPANY.label}
      >
        <DocumentTextField
          placeholder={FIELD.IMPLEMENTER_AND_RELATED_COMPANY.placeholder}
          required={FIELD.IMPLEMENTER_AND_RELATED_COMPANY.required}
          {...register(FIELD.IMPLEMENTER_AND_RELATED_COMPANY.key, {
            minLength: {
              value: FIELD.IMPLEMENTER_AND_RELATED_COMPANY.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.IMPLEMENTER_AND_RELATED_COMPANY.minLength,
                FIELD.IMPLEMENTER_AND_RELATED_COMPANY.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.IMPLEMENTER_AND_RELATED_COMPANY.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.IMPLEMENTER_AND_RELATED_COMPANY.minLength,
                FIELD.IMPLEMENTER_AND_RELATED_COMPANY.maxLength,
              ),
            },
          })}
          errorMessage={
            errors[FIELD.IMPLEMENTER_AND_RELATED_COMPANY.key]?.message
          }
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.LAND_AREA.label}>
        <LandAreaBox>
          <LandAreaField>
            <Controller
              name={FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.key}
              control={control}
              rules={{
                required: "토지면적 입력 필수",
                minLength: {
                  value:
                    FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.minLength,
                  message: formatMinMaxLengthMessage(
                    FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.minLength,
                    FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.maxLength,
                  ),
                },
                maxLength: {
                  value:
                    FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.maxLength,
                  message: formatMinMaxLengthMessage(
                    FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.minLength,
                    FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.maxLength,
                  ),
                },
              }}
              render={({ field }) => (
                <DocumentTextField
                  {...field}
                  ref={(ref) => {
                    field.ref(ref);
                    pyeongRef.current = ref;
                  }}
                  step="0.01"
                  placeholder="0"
                  inputStyle={{ textAlign: "right" }}
                  required={
                    FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.required
                  }
                  onChange={(event) => {
                    const { value } = event.target;
                    const valueWithoutComma = value.replace(/[^0-9]/g, "");

                    // 음수 입력 감지 처리
                    if (value.includes("-")) {
                      setError(
                        FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.key,
                        {
                          type: "validate",
                          message: "1 이상의 숫자만 입력 가능",
                        },
                      );
                      return;
                    } else
                      clearErrors(
                        FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.key,
                      );

                    // 빈 값 처리
                    if (value === "") {
                      field.onChange("");

                      setValue(
                        FIELD.LAND_AREA.fields.LAND_AREA_IN_PYEONG.key,
                        0,
                      );
                      if (pyeongRef.current) {
                        pyeongRef.current.value = "0";
                      }
                      return;
                    }

                    const numberValue = Number(valueWithoutComma);

                    if (/^\d+(\.\d{1,2})?$/.test(valueWithoutComma)) {
                      field.onChange(numberValue);

                      setValue(
                        FIELD.LAND_AREA.fields.LAND_AREA_IN_PYEONG.key,
                        Number((numberValue * METER_TO_PYEONG).toFixed(2)),
                      );
                    } else if (pyeongRef.current) {
                      const fixedValue = numberValue.toFixed(2);
                      const numberFixedValue = Number(fixedValue);

                      pyeongRef.current.value = fixedValue;
                      field.onChange(numberFixedValue);
                    }
                  }}
                  value={formatNumber(
                    watch(
                      FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.key,
                    ),
                  )}
                  errorMessage={
                    get(
                      errors,
                      FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.key,
                    )?.message
                  }
                />
              )}
            />
          </LandAreaField>
          <div style={{ marginRight: "3rem" }}>
            {FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.label}
          </div>

          <LandAreaField>
            <DocumentTextField
              // type={FIELD.LAND_AREA.fields.LAND_AREA_IN_SQUARE_METERS.type}
              placeholder="0"
              inputStyle={{ textAlign: "right" }}
              required={FIELD.LAND_AREA.fields.LAND_AREA_IN_PYEONG.required}
              readOnly
              value={formatNumber(
                watch(FIELD.LAND_AREA.fields.LAND_AREA_IN_PYEONG.key),
              )}
              {...register(FIELD.LAND_AREA.fields.LAND_AREA_IN_PYEONG.key, {
                valueAsNumber: true,
              })}
              errorMessage={
                get(errors, FIELD.LAND_AREA.fields.LAND_AREA_IN_PYEONG.key)
                  ?.message
              }
            />
          </LandAreaField>
          {FIELD.LAND_AREA.fields.LAND_AREA_IN_PYEONG.label}
        </LandAreaBox>
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.URBAN_MANAGEMENT_PLAN.label}>
        <DocumentTextField
          placeholder={FIELD.URBAN_MANAGEMENT_PLAN.placeholder}
          required={FIELD.URBAN_MANAGEMENT_PLAN.required}
          {...register(FIELD.URBAN_MANAGEMENT_PLAN.key, {
            required: "도시관리계획 입력 필수",
            minLength: {
              value: FIELD.URBAN_MANAGEMENT_PLAN.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.URBAN_MANAGEMENT_PLAN.minLength,
                FIELD.URBAN_MANAGEMENT_PLAN.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.URBAN_MANAGEMENT_PLAN.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.URBAN_MANAGEMENT_PLAN.minLength,
                FIELD.URBAN_MANAGEMENT_PLAN.maxLength,
              ),
            },
          })}
          errorMessage={errors[FIELD.URBAN_MANAGEMENT_PLAN.key]?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.BUSINESS_SCALE.label}>
        <DocumentTextField
          placeholder={FIELD.BUSINESS_SCALE.placeholder}
          required={FIELD.BUSINESS_SCALE.required}
          {...register(FIELD.BUSINESS_SCALE.key, {
            minLength: {
              value: FIELD.BUSINESS_SCALE.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.BUSINESS_SCALE.minLength,
                FIELD.BUSINESS_SCALE.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.BUSINESS_SCALE.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.BUSINESS_SCALE.minLength,
                FIELD.BUSINESS_SCALE.maxLength,
              ),
            },
          })}
          errorMessage={errors[FIELD.BUSINESS_SCALE.key]?.message}
        />
      </CreateDocumentForm.Field>

      <CreateDocumentForm.Field label={FIELD.ADDITIONAL_NOTES.label}>
        <DocumentTextAreaField
          placeholder={FIELD.ADDITIONAL_NOTES.placeholder}
          required={FIELD.ADDITIONAL_NOTES.required}
          {...register(FIELD.ADDITIONAL_NOTES.key, {
            minLength: {
              value: FIELD.ADDITIONAL_NOTES.minLength,
              message: formatMinMaxLengthMessage(
                FIELD.ADDITIONAL_NOTES.minLength,
                FIELD.ADDITIONAL_NOTES.maxLength,
              ),
            },
            maxLength: {
              value: FIELD.ADDITIONAL_NOTES.maxLength,
              message: formatMinMaxLengthMessage(
                FIELD.ADDITIONAL_NOTES.minLength,
                FIELD.ADDITIONAL_NOTES.maxLength,
              ),
            },
          })}
          errorMessage={errors[FIELD.ADDITIONAL_NOTES.key]?.message}
        />
      </CreateDocumentForm.Field>
    </CreateDocumentClassFormBody>
  );
};

export default ContractInfoStep1;

/* 스타일 */
const LandAreaBox = styled.div`
  display: flex;
  gap: 0.6rem;
  align-items: baseline;
`;

const LandAreaField = styled.div`
  display: flex;
  align-items: center;

  *:first-child {
    max-width: 10rem;
  }
`;
